import { Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';

export type CatalogueItem = {
  code: string;
  name: string;
  aka: string | null;
  category: string;
  unit: 'each' | 'metre';
  requiresSerial: boolean;
  maxPerVisit: number | null;
};

export type MaterialLine = {
  code: string;
  quantity: number;
  serial?: string;
  note?: string;
};

/**
 * What was fitted or used, filled in on a phone at the end of a job.
 *
 * The stock list is fixed because a drum of fibre logged as "fibre", "Fiber
 * 100m" and "drop wire" is three things no report can add up, and stock nobody
 * can add up is stock that walks. Cable asks for a distance rather than a
 * count, because that is how it is bought and how it is billed — "1 fibre" on
 * a job sheet reconciles against nothing.
 *
 * Serial numbers are compulsory for anything with a resale value. That is the
 * only way a router that goes missing is traced back to the job it left on.
 *
 * Built for one thumb outdoors: search first, big tap targets, and only the
 * items actually picked stay expanded.
 */
export function MaterialsChecklist({
  catalogue,
  categoryLabels,
  lines,
  other,
  onChange,
  onOtherChange,
}: {
  catalogue: CatalogueItem[];
  categoryLabels: Record<string, string>;
  lines: MaterialLine[];
  other: string;
  onChange: (lines: MaterialLine[]) => void;
  onOtherChange: (value: string) => void;
}) {
  const [query, setQuery] = useState('');

  const byCode = useMemo(() => new Map(catalogue.map((c) => [c.code, c])), [catalogue]);
  const picked = useMemo(() => new Map(lines.map((l) => [l.code, l])), [lines]);

  const grouped = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matching = needle
      ? catalogue.filter(
          (item) =>
            item.name.toLowerCase().includes(needle) ||
            item.aka?.toLowerCase().includes(needle) ||
            item.code.toLowerCase().includes(needle),
        )
      : catalogue;

    const map = new Map<string, CatalogueItem[]>();
    for (const item of matching) {
      map.set(item.category, [...(map.get(item.category) ?? []), item]);
    }
    return [...map.entries()];
  }, [catalogue, query]);

  const setLine = (code: string, patch: Partial<MaterialLine> | null) => {
    if (patch === null) {
      onChange(lines.filter((l) => l.code !== code));
      return;
    }
    const existing = picked.get(code);
    onChange(
      existing
        ? lines.map((l) => (l.code === code ? { ...l, ...patch } : l))
        : [...lines, { code, quantity: 1, ...patch }],
    );
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold">What did you use?</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Tap an item, then say how many. Cable is measured in metres.
        </p>
      </div>

      {/* Picked items first: on a long list, what you have already chosen must
          not scroll away while you add the next one. */}
      {lines.length > 0 && (
        <ul className="space-y-2 rounded-xl bg-primary-50/60 p-3">
          {lines.map((line) => {
            const item = byCode.get(line.code);
            if (!item) return null;
            const overMax = item.maxPerVisit !== null && line.quantity > item.maxPerVisit;

            return (
              <li key={line.code} className="rounded-lg bg-card p-3 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium">{item.name}</span>
                  <button
                    type="button"
                    onClick={() => setLine(line.code, null)}
                    className="rounded-lg p-1 text-muted-foreground hover:bg-muted"
                    aria-label={`Remove ${item.name}`}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-2 flex items-center gap-2">
                  <label className="flex-1">
                    <span className="mb-1 block text-xs text-muted-foreground">
                      {item.unit === 'metre' ? 'Distance used (metres)' : 'How many'}
                    </span>
                    <input
                      type="number"
                      inputMode="decimal"
                      min={item.unit === 'metre' ? 0.5 : 1}
                      step={item.unit === 'metre' ? 0.5 : 1}
                      className="input w-full text-lg"
                      value={line.quantity}
                      onChange={(e) =>
                        setLine(line.code, { quantity: Number(e.target.value) || 0 })
                      }
                    />
                  </label>
                  <span className="mt-5 text-sm text-muted-foreground">
                    {item.unit === 'metre' ? 'm' : 'pcs'}
                  </span>
                </div>

                {overMax && (
                  <p className="mt-1 text-xs text-danger-600">
                    That is more than the {item.maxPerVisit}
                    {item.unit === 'metre' ? 'm' : ''} usually needed in one visit. Check the number.
                  </p>
                )}

                {item.requiresSerial && (
                  <label className="mt-2 block">
                    <span className="mb-1 block text-xs text-muted-foreground">
                      Serial number
                      {line.quantity > 1 ? ` — ${line.quantity}, separated by commas` : ''}
                    </span>
                    <input
                      className="input w-full"
                      value={line.serial ?? ''}
                      placeholder="From the sticker on the box"
                      onChange={(e) => setLine(line.code, { serial: e.target.value })}
                    />
                    {!line.serial?.trim() && (
                      <span className="mt-1 block text-xs text-accent-600">
                        Needed — this is how it is traced if it goes missing.
                      </span>
                    )}
                  </label>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          className="input w-full pl-9"
          placeholder="Search — FAT, router, Cat 6…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search the stock list"
        />
      </div>

      <div className="space-y-3">
        {grouped.map(([category, items]) => (
          <div key={category}>
            <h4 className="mb-1.5 text-xs font-semibold label-mono tracking-[0.12em] text-muted-foreground">
              {categoryLabels[category] ?? category}
            </h4>
            <div className="flex flex-wrap gap-2">
              {items.map((item) => {
                const on = picked.has(item.code);
                return (
                  <button
                    key={item.code}
                    type="button"
                    onClick={() => setLine(item.code, on ? null : { quantity: 1 })}
                    aria-pressed={on}
                    className={`rounded-xl border px-3 py-2 text-left text-sm ${
                      on
                        ? 'border-primary-500 bg-primary-50 text-primary-800'
                        : 'border-border bg-card'
                    }`}
                  >
                    {item.name}
                    {item.aka && (
                      <span className="ml-1 text-xs text-muted-foreground">({item.aka})</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        {grouped.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nothing matches “{query}”. Put it in other items below.
          </p>
        )}
      </div>

      <label className="block">
        <span className="mb-1 block text-sm font-medium">Anything else you used</span>
        <span className="mb-1.5 block text-xs text-muted-foreground">
          Anything not on the list above. Say what it was and how much.
        </span>
        <textarea
          className="input w-full"
          rows={3}
          value={other}
          placeholder="e.g. 2 rolls insulation tape, 1 padlock for the cabinet"
          onChange={(e) => onOtherChange(e.target.value)}
        />
      </label>
    </div>
  );
}
